// ==============================
// File: src/lib/nav.js
// Canonical navigation helpers to prevent unintended redirects
// All navigation should use these helpers instead of bare navigate() calls
// ==============================

// ========== ROUTE CONSTANTS ==========
// Use these constants throughout the app to ensure consistency

// Dashboard (Studio) routes - all prefixed with /dashboard
export const ROUTES = {
  // Top-level
  DASHBOARD_HOME: "/dashboard/home",
  DASHBOARD: "/dashboard",
  
  // Main features
  GALLERY: "/dashboard/gallery",
  PLANNER: "/dashboard/planner",
  PLANNER_TASKS: "/dashboard/planner/tasks",
  PLANNER_GANTT: "/dashboard/planner/gantt",
  
  // Production
  BATCHES: "/dashboard/batches",
  BATCH_VIEW: "/dashboard/batches/:id", // Use with routeFor('BATCH_VIEW', { id: '123' })
  KILN_LOGS: "/dashboard/kiln-logs",
  KILN_LOGS_NEW: "/dashboard/kiln-logs/new",
  KILN_PLANNER: "/dashboard/kiln-planner",
  
  // Resources
  MATERIALS: "/dashboard/materials",
  PURCHASE_ORDERS: "/dashboard/purchase-orders",
  RECEIPTS: "/dashboard/receipts",
  RECEIPTS_LOG: "/dashboard/receipts-log",
  KILNS: "/dashboard/kilns",
  
  // Workshops & Participants
  WORKSHOPS: "/dashboard/workshops",
  PARTICIPANTS: "/dashboard/participants",
  MY_PIECES: "/dashboard/my-pieces",
  
  // Management
  STAFF: "/dashboard/staff",
  PACKAGING_LIBRARY: "/dashboard/packaging/library",
  PACKAGING_RULES: "/dashboard/packaging/rules",
  MARKETS_CHECKLISTS: "/dashboard/markets-checklists",
  
  // Settings & Tools
  SETTINGS: "/dashboard/settings",
  SETTINGS_DATA: "/dashboard/settings/data",
  SETTINGS_STAFF_TARGETS: "/dashboard/settings/staff-targets",
  HELP: "/dashboard/help",
  ANALYTICS: "/dashboard/analytics",
  TIME_LOGS: "/dashboard/time-logs",
  TERMS: "/dashboard/legal/terms",
  NOTIFICATIONS: "/dashboard/notifications",
  
  // Misc
  SHOP: "/dashboard/shop",
  FORUM: "/dashboard/forum",
  EXPLORE: "/dashboard/explore",
  INSPIRATION: "/dashboard/inspiration",
  JOURNAL: "/dashboard/journal",
  TEMPLATES: "/dashboard/templates",
  
  // Visitor (Discover) routes - all prefixed with /discover
  DISCOVER: "/discover",
  DISCOVER_MAP: "/discover/map",
  DISCOVER_WORKSHOPS: "/discover/workshops",
  DISCOVER_MARKETPLACE: "/discover/marketplace",
  DISCOVER_STUDIO: "/discover/studio/:slug", // Use with routeFor('DISCOVER_STUDIO', { slug: 'studio-name' })
  
  // Auth
  LOGIN: "/login",
  START: "/start",
};

// ========== HELPER FUNCTIONS ==========

/**
 * Get the absolute path for a route, substituting parameters
 * @param {string} routeKey - Key from ROUTES object (e.g., 'BATCH_VIEW')
 * @param {object} params - Parameters to substitute (e.g., { id: '123' })
 * @returns {string} - Absolute path (e.g., '/dashboard/batches/123')
 * @example
 *   routeFor('BATCH_VIEW', { id: '123' }) => '/dashboard/batches/123'
 *   routeFor('DISCOVER_STUDIO', { slug: 'pottery-hub' }) => '/discover/studio/pottery-hub'
 */
export function routeFor(routeKey, params = {}) {
  let path = ROUTES[routeKey];
  
  if (!path) {
    console.warn(`[nav] Unknown route key: ${routeKey}. Falling back to /dashboard`);
    return ROUTES.DASHBOARD;
  }
  
  // Substitute URL parameters
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });
  
  return path;
}

/**
 * Navigate to a route using the canonical navigation helper
 * Adds dev-mode logging and regression detection
 * @param {function} navigate - React Router's navigate function
 * @param {string} routeKeyOrPath - Route key from ROUTES or absolute path
 * @param {object} options - { params, replace, state, source }
 * @example
 *   navigateTo(navigate, 'WORKSHOPS')
 *   navigateTo(navigate, 'BATCH_VIEW', { params: { id: '123' } })
 *   navigateTo(navigate, '/dashboard/materials', { replace: true })
 */
export function navigateTo(navigate, routeKeyOrPath, options = {}) {
  const { params = {}, replace = false, state = null, source = 'unknown' } = options;
  
  // Resolve the path
  let path;
  if (ROUTES[routeKeyOrPath]) {
    path = routeFor(routeKeyOrPath, params);
  } else if (routeKeyOrPath.startsWith('/')) {
    path = routeKeyOrPath;
  } else {
    console.warn(`[nav] Invalid route: ${routeKeyOrPath}. Must be a ROUTES key or absolute path.`);
    path = ROUTES.DASHBOARD;
  }
  
  // Dev-mode logging
  if (import.meta.env.DEV) {
    const currentPath = window.location.pathname;
    const isDashboardContext = currentPath.startsWith('/dashboard');
    const isNavigatingToDiscover = path.startsWith('/discover');
    
    console.log(`[nav] ${source} → ${path}`, {
      from: currentPath,
      to: path,
      replace,
      state,
    });
    
    // Regression warning: navigating to visitor world from dashboard context
    if (isDashboardContext && isNavigatingToDiscover) {
      console.warn(
        `[nav] ⚠️ REGRESSION WARNING: Navigating from dashboard to visitor world!`,
        { from: currentPath, to: path, source }
      );
    }
  }
  
  // Execute navigation
  navigate(path, { replace, state });
}

/**
 * Get the absolute path for a relative path based on current context
 * DEPRECATED: Use routeFor() and navigateTo() with ROUTES constants instead
 * @param {string} relativePath - Path like "/workshops" or "workshops"
 * @param {string} currentPath - Current pathname from location
 * @returns {string} - Absolute path
 */
export function getAbsolutePath(relativePath, currentPath = '/') {
  // If already absolute, return as-is
  if (relativePath.startsWith('/dashboard') || relativePath.startsWith('/discover')) {
    return relativePath;
  }
  
  // Ensure leading slash
  const normalized = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  
  // Determine context
  if (currentPath.startsWith('/dashboard')) {
    return `/dashboard${normalized}`;
  } else if (currentPath.startsWith('/discover')) {
    return `/discover${normalized}`;
  }
  
  // Default to dashboard for studio features
  return `/dashboard${normalized}`;
}

/**
 * Check if a path is in the dashboard (studio) context
 * @param {string} path - Path to check
 * @returns {boolean}
 */
export function isDashboardPath(path) {
  return path.startsWith('/dashboard');
}

/**
 * Check if a path is in the discover (visitor) context
 * @param {string} path - Path to check
 * @returns {boolean}
 */
export function isDiscoverPath(path) {
  return path.startsWith('/discover');
}

// ========== LEGACY WIDGET HELPERS ==========
// For gradual migration - these will be deprecated once all widgets are updated

/**
 * Get the absolute path for a widget by ID/key
 * @deprecated Use ROUTES constants and navigateTo() instead
 * @param {string} widgetId - Widget identifier
 * @returns {string} - Absolute dashboard path
 */
export function widgetPathById(widgetId) {
  const widgetRoutes = {
    Workshops: ROUTES.WORKSHOPS,
    KilnTracker: ROUTES.KILN_LOGS,
    Materials: ROUTES.MATERIALS,
    MaterialDetails: ROUTES.MATERIALS,
    Production: ROUTES.BATCHES,
    Tasks: ROUTES.PLANNER_TASKS,
    Receipts: ROUTES.RECEIPTS,
    ReorderPO: ROUTES.PURCHASE_ORDERS,
    Participants: ROUTES.MY_PIECES,
    Analytics: ROUTES.ANALYTICS,
    Staff: ROUTES.STAFF,
    MarketsChecklist: ROUTES.MARKETS_CHECKLISTS,
  };
  
  return widgetRoutes[widgetId] || ROUTES.DASHBOARD;
}

/**
 * Navigate to a widget by ID
 * @deprecated Use navigateTo() with ROUTES constants instead
 * @param {function} navigate - React Router's navigate function
 * @param {string} widgetId - Widget identifier
 * @param {object} options - Navigation options
 */
export function navigateToWidget(navigate, widgetId, options = {}) {
  const path = widgetPathById(widgetId);
  navigateTo(navigate, path, { ...options, source: `widget:${widgetId}` });
}
